import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { NavItem } from '@solote/contracts';
import { useAuth, useRoles } from '@/core/auth/AuthProvider';
import { ADVERTISER_ROLES, useHomeRoute } from '@/core/auth/permissions';
import { MetaPixel } from '@/core/analytics/MetaPixel';
import { Marca } from '@/core/brand/Marca';
import { useConfig, useText } from '@/core/config/ConfigProvider';
import { Button, ButtonLink, Container, Skeleton, cx } from '@/design-system';

/**
 * ESQUELETO DO PORTAL
 *
 * Cabeçalho fixo com marca, navegação e a chamada de anunciar — o padrão
 * consagrado em portal imobiliário, porque a ação mais valiosa da página
 * (cadastrar imóvel) não pode depender de rolagem.
 *
 * Os itens de menu saem de `site.navigation`. Não existe lista de links
 * escrita aqui.
 */

function visibleItems(items: NavItem[], roles: string[], planSlug: string | null): NavItem[] {
  return items
    .filter((item) => item.publication.enabled)
    .filter((item) => item.gate.roles.length === 0 || item.gate.roles.some((role) => roles.includes(role)))
    .filter((item) => item.gate.plans.length === 0 || (planSlug !== null && item.gate.plans.includes(planSlug)))
    .sort((a, b) => a.publication.order - b.publication.order);
}

function NavLinks({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cx(
      'rounded-md px-3 py-2 text-[0.9375rem] font-medium no-underline transition-colors',
      isActive ? 'text-primary' : 'text-ink-soft hover:text-ink',
    );

  return (
    <>
      {items.map((item) =>
        item.external ? (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={onNavigate}
            className="rounded-md px-3 py-2 text-[0.9375rem] font-medium text-ink-soft no-underline hover:text-ink"
          >
            {item.label}
          </a>
        ) : (
          <NavLink key={item.key} to={item.href} onClick={onNavigate} className={linkClass}>
            {item.label}
          </NavLink>
        ),
      )}
    </>
  );
}

/*
  A marca saiu daqui para `core/brand/Marca`.

  Ela era desenhada neste arquivo e, por consequencia, so existia no portal.
  As telas de entrar, cadastrar e aceitar convite ficavam sem marca nenhuma —
  e sao as que pedem senha, onde reconhecer o site e o que separa o portal de
  uma copia. Agora e um componente so, e publicar um logotipo novo troca em
  todos ao mesmo tempo.
*/

export function PortalLayout() {
  const { site } = useConfig();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  /*
    SAIR TAMBEM SAI DA TELA.

    `logout` so derrubava a sessao; a pessoa continuava parada em
    `/painel/anuncios`, agora sem sessao. Quem a tirava dali era o guardiao da
    rota — e ao fazer isso ele GRAVAVA o caminho como "de onde vim", para
    devolver a pessoa depois de entrar.

    So que esse bilhete nao tem dono: a proxima pessoa a usar a aba era levada
    para a tela da anterior. Um administrador entrava e caia no painel do
    anunciante.

    Sair pela porta da frente, para a home, nao deixa bilhete nenhum.
  */
  const sair = async () => {
    await logout();
    navigate('/', { replace: true });
  };
  const roles = useRoles();
  const t = useText();
  // O link do usuario leva ao painel DELE: admin para equipe interna,
  // area do anunciante para quem anuncia.
  const homeRoute = useHomeRoute();

  /*
    A CHAMADA DE ANUNCIAR NÃO SOME DEPOIS DO CADASTRO.

    Ela aparecia só para quem estava deslogado, e desaparecia justamente para
    o público a que serve: quem já tem conta de anunciante. O caminho existia
    pelo painel, mas a ação mais valiosa do portal passava a exigir dois
    cliques e saber onde procurar.

    Quem não anuncia — comprador, ou conta interna de moderação — continua sem
    o botão: para essas pessoas ele levaria a uma tela que não é delas.
  */
  const podeAnunciar = Boolean(user) && ADVERTISER_ROLES.some((papel) => user!.roles.includes(papel));
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Navegar fecha o menu do celular. Sem isso ele fica aberto por cima da
  // página nova, e o usuário acha que o clique não funcionou.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const header = visibleItems(site.navigation.header, roles, user?.planSlug ?? null);
  const footer = visibleItems(site.navigation.footer, roles, user?.planSlug ?? null);

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-fg"
      >
        Pular para o conteúdo
      </a>

      <header className="sticky top-0 z-40 border-b border-line bg-surface/95 backdrop-blur-sm">
        <Container>
          <div className="flex h-16 items-center justify-between gap-4">
            <Marca />

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Menu principal">
              <NavLinks items={header} />
            </nav>

            <div className="hidden items-center gap-2 lg:flex">
              {user ? (
                <>
                  <Link
                    to={homeRoute}
                    className="rounded-md px-3 py-2 text-[0.9375rem] font-medium text-ink no-underline hover:bg-surface-muted"
                  >
                    {user.name.split(' ')[0]}
                  </Link>
                  <Button variant="ghost" size="sm" onClick={() => void sair()}>
                    Sair
                  </Button>
                  {podeAnunciar ? (
                    <ButtonLink href="/anunciar" size="sm">
                      {t('nav.advertise', undefined, 'Anunciar imóvel')}
                    </ButtonLink>
                  ) : null}
                </>
              ) : (
                <>
                  <Link
                    to="/entrar"
                    className="rounded-md px-3 py-2 text-[0.9375rem] font-medium text-ink-soft no-underline hover:text-ink"
                  >
                    {t('nav.login', undefined, 'Entrar')}
                  </Link>
                  <ButtonLink href="/anunciar" size="sm">
                    {t('nav.advertise', undefined, 'Anunciar imóvel')}
                  </ButtonLink>
                </>
              )}
            </div>

            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-md text-ink hover:bg-surface-muted lg:hidden"
              aria-expanded={menuOpen}
              aria-controls="menu-mobile"
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
                )}
              </svg>
            </button>
          </div>
        </Container>

        {menuOpen ? (
          <div id="menu-mobile" className="border-t border-line bg-surface lg:hidden">
            <Container>
              <nav className="flex flex-col gap-1 py-3" aria-label="Menu principal">
                <NavLinks items={header} onNavigate={() => setMenuOpen(false)} />
                <div className="mt-2 flex flex-col gap-2 border-t border-line pt-3">
                  {user ? (
                    <>
                      <Link to={homeRoute} className="px-3 py-2 font-medium text-ink no-underline">
                        Meu painel
                      </Link>
                      {podeAnunciar ? (
                        <ButtonLink href="/anunciar" block>
                          {t('nav.advertise', undefined, 'Anunciar imóvel')}
                        </ButtonLink>
                      ) : null}
                      <Button variant="outline" onClick={() => void sair()}>
                        Sair
                      </Button>
                    </>
                  ) : (
                    <>
                      <Link to="/entrar" className="px-3 py-2 font-medium text-ink no-underline">
                        {t('nav.login', undefined, 'Entrar')}
                      </Link>
                      <ButtonLink href="/anunciar" block>
                        {t('nav.advertise', undefined, 'Anunciar imóvel')}
                      </ButtonLink>
                    </>
                  )}
                </div>
              </nav>
            </Container>
          </div>
        ) : null}
      </header>

      <main id="conteudo" className="flex-1">
        <Suspense
          fallback={
            <Container>
              <div className="flex flex-col gap-4 py-12">
                <Skeleton className="h-10 w-2/5" />
                <Skeleton className="h-64 w-full" />
              </div>
            </Container>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      {/*
        AQUI, e nao no `index.html`.

        Este layout e a raiz de TODAS as rotas, entao o componente enxerga
        cada troca de pagina — que e o que o trecho colado no HTML nao faz
        numa SPA: ele contaria so a primeira abertura.

        E daqui ele consegue nao rastrear as areas internas, que o `<head>`
        nao teria como distinguir.
      */}
      <MetaPixel />

      <footer className="mt-16 border-t border-line bg-surface">
        <Container>
          <div className="flex flex-col gap-8 py-12">
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="flex max-w-sm flex-col gap-3">
                <Marca />
                {site.brand.tagline ? <p className="text-sm text-ink-soft">{site.brand.tagline}</p> : null}
              </div>

              <nav className="flex flex-col gap-2" aria-label="Menu do rodapé">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink-faint">Institucional</span>
                {footer.map((item) => (
                  <Link key={item.key} to={item.href} className="text-sm text-ink-soft no-underline hover:text-primary">
                    {item.label}
                  </Link>
                ))}
              </nav>

              <div className="flex flex-col gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink-faint">Atendimento</span>
                {site.brand.supportEmail ? (
                  <a href={`mailto:${site.brand.supportEmail}`} className="text-sm text-ink-soft no-underline hover:text-primary">
                    {site.brand.supportEmail}
                  </a>
                ) : null}
                {site.brand.supportWhatsapp ? (
                  <a
                    href={`https://wa.me/55${site.brand.supportWhatsapp}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-sm text-ink-soft no-underline hover:text-primary"
                  >
                    WhatsApp
                  </a>
                ) : null}
              </div>
            </div>

            <p className="border-t border-line pt-6 text-xs text-ink-faint">
              © {new Date().getFullYear()} {site.brand.name}. {t('footer.legal', undefined, '')}
            </p>
          </div>
        </Container>
      </footer>
    </div>
  );
}
